import React, { useState, useRef, useEffect } from 'react';
import '../css/selectFieldInline.css';
import { ChevronIcon, SearchIcon } from '../../../../../asset-controller-mod/asset-serv/icons/iconExporter';

export interface SelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

export interface SelectFieldInlineProps {
  id?: string;
  name?: string;
  value?: string;
  placeholder?: string;
  options: SelectOption[];
  onChange?: (value: string) => void;
  onFocus?: (e: React.FocusEvent<HTMLDivElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLDivElement>) => void;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  searchable?: boolean;
  assistiveText?: string;
  error?: boolean;
  errorText?: string;
  warning?: boolean;
}

const SelectFieldInline: React.FC<SelectFieldInlineProps> = ({
  id,
  name,
  value,
  placeholder,
  options,
  onChange,
  onFocus,
  onBlur,
  required = false,
  disabled = false,
  className = '',
  searchable = false,
  assistiveText,
  error = false,
  errorText,
  warning = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(opt => opt.value === value);
  const hasValue = value && value.length > 0;

  const filteredOptions = searchable
    ? options.filter(option =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    )
    : options;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onChange) {
      onChange(optionValue);
    }
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div
      ref={containerRef}
      className={`scl--cms-components-selectFieldInline-container ${className}`}
    >
      <div //NOSONAR
        className={`scl--cms-components-selectFieldInline-plate ${error ? 'scl--cms-components-selectFieldInline--error' : ''} ${warning ? 'scl--cms-components-selectFieldInline--warning' : ''} ${disabled ? 'scl--cms-components-selectFieldInline--disabled' : ''} ${isOpen ? 'scl--cms-components-selectFieldInline--open' : ''}`}
        onClick={handleToggle}
      >
        <div className="scl--cms-components-selectFieldInline-wrapper">
          <div className="scl--cms-components-selectFieldInline-valueWrapper">
            <div className="scl--cms-components-selectFieldInline-textWrapper">

              {/* Show selected value or placeholder — no floating label */}
              {hasValue ? (
                <span className="scl--cms-components-selectFieldInline-text">
                  {selectedOption?.icon && (
                    <span className="scl--cms-components-selectFieldInline-selectedIcon">
                      {selectedOption.icon}
                    </span>
                  )}
                  {selectedOption?.label}
                </span>
              ) : (
                <span className="scl--cms-components-selectFieldInline-placeholder">
                  {placeholder}
                </span>
              )}

            </div>
            <ChevronIcon className="scl--cms-components-selectFieldInline-chevronIcon" />
          </div>
        </div>
      </div>

      <div className={`scl--cms-components-selectFieldInline-dropdown ${isOpen ? 'scl--cms-components-selectFieldInline-dropdown--open' : ''}`}>
        {searchable && (
          <div className="scl--cms-components-selectFieldInline-search">
            <SearchIcon className="scl--cms-components-selectFieldInline-searchIcon" />
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={handleSearchChange}
              className="scl--cms-components-selectFieldInline-searchInput"
              onClick={e => e.stopPropagation()}
              autoFocus={isOpen}
              autoComplete="off"
            />
          </div>
        )}

        <div className="scl--cms-components-selectFieldInline-options">
          {filteredOptions.length > 0 ? (
            filteredOptions.map(option => (
              <div //NOSONAR
                key={option.value}
                className={`scl--cms-components-selectFieldInline-option ${value === option.value ? 'scl--cms-components-selectFieldInline-option--selected' : ''}`}
                onClick={e => handleSelect(option.value, e)}
              >
                {option.icon && (
                  <span className="scl--cms-components-selectFieldInline-optionIcon">
                    {option.icon}
                  </span>
                )}
                <span className="scl--cms-components-selectFieldInline-optionLabel">
                  {option.label}
                </span>
              </div>
            ))
          ) : (
            <div className="scl--cms-components-selectFieldInline-noOptions">
              No options found
            </div>
          )}
        </div>
      </div>

      {(assistiveText || errorText) && (
        <div className={`scl--cms-components-selectFieldInline-assistive ${error ? 'scl--cms-components-selectFieldInline-assistive--error' : ''}`}>
          {error && errorText ? errorText : assistiveText}
        </div>
      )}

      {/* Hidden select for form submission */}
      <select
        id={id}
        name={name}
        value={value}
        onChange={e => onChange?.(e.target.value)}
        required={required}
        disabled={disabled}
        style={{ display: 'none' }}
      >
        <option value="">{placeholder}</option>
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export default SelectFieldInline;